/**
 * Keyboard navigation extension for HeaderMenu.
 *
 * Adds:
 * - Enter/Space: opens dropdown for items with submenus
 * - Escape: closes open dropdown and returns focus to trigger
 * - ArrowDown/ArrowUp: moves focus between dropdown items
 * - ArrowLeft/ArrowRight: moves focus between top-level nav items
 * - Focus trap within open dropdown (Tab wraps within submenu)
 * - Visible focus ring enforcement
 *
 * This script extends the existing header-menu custom element without
 * replacing it. It listens on the <header-menu> element.
 */

(function () {
  function init() {
    const headerMenu = document.querySelector('header-menu');
    if (!headerMenu) return;

    /** @type {HTMLElement | null} */
    let activeSubmenu = null;
    /** @type {HTMLElement | null} */
    let activeTrigger = null;

    /**
     * Get all top-level menu item links
     * @returns {HTMLElement[]}
     */
    function getTopLevelItems() {
      return Array.from(
        headerMenu.querySelectorAll(':scope > li > [ref="menuitem"], :scope > li > button[role="menuitem"]')
      ).filter(function (el) {
        // Only items in the default slot (not overflow)
        return !el.closest('[slot="overflow"]');
      });
    }

    /**
     * Get all focusable elements within a submenu
     * @param {HTMLElement} submenu
     * @returns {HTMLElement[]}
     */
    function getFocusableItems(submenu) {
      return Array.from(
        submenu.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter(function (el) {
        return el.offsetParent !== null; // Only visible elements
      });
    }

    /**
     * Open a submenu for a given trigger element
     * @param {HTMLElement} trigger
     */
    function openSubmenu(trigger) {
      const li = trigger.closest('li');
      if (!li) return;

      const submenu = li.querySelector('[ref="submenu[]"]');
      if (!submenu) return;

      // Use the existing activate mechanism via focus event
      trigger.dispatchEvent(new FocusEvent('focus', { bubbles: true }));

      activeSubmenu = submenu;
      activeTrigger = trigger;
      trigger.setAttribute('aria-expanded', 'true');

      // Focus first link in submenu after a brief delay for animation
      requestAnimationFrame(function () {
        var items = getFocusableItems(submenu);
        if (items.length > 0) {
          items[0].focus();
        }
      });
    }

    /**
     * Close the currently active submenu
     */
    function closeSubmenu() {
      if (!activeTrigger) return;

      activeTrigger.setAttribute('aria-expanded', 'false');
      activeTrigger.dispatchEvent(new FocusEvent('blur', { bubbles: true }));

      var trigger = activeTrigger;
      activeSubmenu = null;
      activeTrigger = null;

      // Return focus to trigger
      trigger.focus();
    }

    // ─── Keyboard handler on the entire header-menu ───
    headerMenu.addEventListener('keydown', function (e) {
      var key = e.key;
      var target = /** @type {HTMLElement} */ (e.target);
      var topItems = getTopLevelItems();
      var currentTopIndex = topItems.indexOf(target);

      // ── Top-level nav items ──
      if (currentTopIndex !== -1) {
        switch (key) {
          case 'Enter':
          case ' ':
            // If this item has a submenu, open it instead of navigating
            var li = target.closest('li');
            if (li && li.querySelector('[ref="submenu[]"]')) {
              e.preventDefault();
              openSubmenu(target);
            }
            // Otherwise, let the default <a> navigation happen
            break;

          case 'ArrowRight':
            e.preventDefault();
            var nextIndex = (currentTopIndex + 1) % topItems.length;
            topItems[nextIndex].focus();
            break;

          case 'ArrowLeft':
            e.preventDefault();
            var prevIndex = (currentTopIndex - 1 + topItems.length) % topItems.length;
            topItems[prevIndex].focus();
            break;

          case 'ArrowDown':
            // Open submenu if it exists
            var liDown = target.closest('li');
            if (liDown && liDown.querySelector('[ref="submenu[]"]')) {
              e.preventDefault();
              openSubmenu(target);
            }
            break;

          case 'Escape':
            closeSubmenu();
            break;
        }
        return;
      }

      // ── Inside an open submenu ──
      if (activeSubmenu && activeSubmenu.contains(target)) {
        var focusable = getFocusableItems(activeSubmenu);
        var idx = focusable.indexOf(target);

        switch (key) {
          case 'Escape':
            e.preventDefault();
            closeSubmenu();
            break;

          case 'ArrowDown':
            e.preventDefault();
            if (idx < focusable.length - 1) {
              focusable[idx + 1].focus();
            } else {
              focusable[0].focus(); // Wrap to top
            }
            break;

          case 'ArrowUp':
            e.preventDefault();
            if (idx > 0) {
              focusable[idx - 1].focus();
            } else {
              focusable[focusable.length - 1].focus(); // Wrap to bottom
            }
            break;

          case 'Tab':
            // Trap focus within submenu
            if (focusable.length === 0) break;

            if (e.shiftKey) {
              if (idx <= 0) {
                e.preventDefault();
                focusable[focusable.length - 1].focus();
              }
            } else {
              if (idx >= focusable.length - 1) {
                e.preventDefault();
                focusable[0].focus();
              }
            }
            break;
        }
      }
    });

    // ─── Close submenu when focus leaves header entirely ───
    headerMenu.addEventListener('focusout', function (e) {
      // Use requestAnimationFrame to check after focus has moved
      requestAnimationFrame(function () {
        if (!headerMenu.contains(document.activeElement) && activeSubmenu) {
          closeSubmenu();
        }
      });
    });
  }

  // Run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
